import type { AppSettings, DashboardMember, DashboardSummary, IdentitySuggestion } from "../shared/types";

let csrfToken: string | null = null;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(pathname: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (options.method && !["GET", "HEAD"].includes(options.method.toUpperCase()) && csrfToken) {
    headers.set("x-csrf-token", csrfToken);
  }
  let response: Response;
  try {
    response = await fetch(`/api${pathname}`, {
      ...options,
      headers,
      credentials: "include",
    });
  } catch {
    throw new ApiError(
      "Dashboard connection was interrupted. Refresh to confirm whether the action completed before retrying.",
      0,
    );
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(String(body.error ?? `Request failed (${response.status})`), response.status);
  return body as T;
}

export const api = {
  async session() {
    const session = await request<{ authenticated: boolean; csrfToken: string; expiresAt: string }>("/auth/session");
    csrfToken = session.csrfToken;
    return session;
  },
  async login(password: string) {
    const session = await request<{ authenticated: boolean; csrfToken: string; expiresAt: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
    csrfToken = session.csrfToken;
    return session;
  },
  async logout() {
    await request("/auth/logout", { method: "POST" });
    csrfToken = null;
  },
  summary: () => request<DashboardSummary>("/summary"),
  members: (params: { bucket?: string; search?: string; page?: number; pageSize?: number } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") query.set(key, String(value));
    }
    return request<{ items: DashboardMember[]; total: number; page: number; pageSize: number }>(`/members?${query}`);
  },
  unlinkedWhop: (entitledOnly = false) =>
    request<{
      items: Array<{
        id: string;
        username: string | null;
        name: string | null;
        email: string | null;
        membershipCount: number;
        statuses: string[];
        products: string[];
      }>;
    }>(`/whop/unlinked${entitledOnly ? "?entitled=true" : ""}`),
  identitySuggestions: () => request<{ items: IdentitySuggestion[] }>("/identity/suggestions"),
  whitelist: (memberId: number, input: { note: string; expiresAt?: string | null }) =>
    request(`/members/${memberId}/whitelist`, { method: "POST", body: JSON.stringify(input) }),
  removeWhitelist: (memberId: number) => request(`/members/${memberId}/whitelist`, { method: "DELETE" }),
  removeMember: (memberId: number) => request<{ jobId: string }>(`/members/${memberId}/remove`, { method: "POST" }),
  bulk: (memberIds: number[], action: "whitelist" | "remove", note?: string) =>
    request<{ results: Array<{ memberId: number; ok: boolean; jobId?: string; error?: string }> }>("/members/bulk", {
      method: "POST",
      body: JSON.stringify({ memberIds, action, note }),
    }),
  link: (memberId: number, whopUserId: string) =>
    request(`/members/${memberId}/link`, { method: "POST", body: JSON.stringify({ whopUserId }) }),
  unlink: (memberId: number) => request(`/members/${memberId}/link`, { method: "DELETE" }),
  settings: () => request<AppSettings>("/settings"),
  updateSettings: (patch: Partial<AppSettings>) =>
    request<AppSettings>("/settings", { method: "PATCH", body: JSON.stringify(patch) }),
  armAutomation: (confirmation: string) =>
    request<AppSettings>("/automation/arm", { method: "POST", body: JSON.stringify({ confirmation }) }),
  lockRemovals: () => request<AppSettings>("/removals/lock", { method: "POST" }),
  unlockRemovals: (confirmation: string) =>
    request<AppSettings>("/removals/unlock", {
      method: "POST",
      body: JSON.stringify({ confirmation }),
    }),
  sync: (source: "whop" | "telegram" | "all") => request<{ jobIds: string[] }>(`/sync/${source}`, { method: "POST" }),
  reconcile: () => request<{ jobId: string }>("/reconcile", { method: "POST" }),
  jobs: () => request<{ items: JobItem[] }>("/jobs"),
  audit: () => request<{ items: AuditItem[] }>("/audit"),
};

export interface JobItem {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  status: string;
  priority: number;
  runAfter: string;
  attempts: number;
  maxAttempts: number;
  error: string | null;
  result: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuditItem {
  id: string;
  actorType: string;
  actorId: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  detail: Record<string, unknown>;
  createdAt: string;
}
